import { useMemo } from 'react';

export type RoomJoinErrorCode =
  | 'handshake_timeout'
  | 'signalr_failed'
  | 'spawn_failed'
  | 'join_ack_timeout'
  | 'unknown';

export interface RoomJoinErrorState {
  code: RoomJoinErrorCode;
  message?: string;
  correlationId?: string;
}

interface RoomJoinErrorProps {
  state: RoomJoinErrorState;
  onRetry: () => void;
  onCancel?: () => void;
}

// AC 1.4: replaces the silent fake-room fallback. Renders typed states with a
// retry path. Copy is intentionally plain — the parent surface is responsible
// for theming.
export function RoomJoinError({ state, onRetry, onCancel }: RoomJoinErrorProps) {
  const { title, body } = useMemo(() => copyForCode(state.code), [state.code]);

  return (
    <div className="room-join-error" role="alert" data-code={state.code}>
      <h3>{title}</h3>
      <p>{body}</p>
      {state.message && <p className="room-join-error__detail">{state.message}</p>}
      {state.correlationId && (
        <p className="room-join-error__cid">
          ref: <code>{state.correlationId}</code>
        </p>
      )}
      <div className="room-join-error__actions">
        <button type="button" onClick={onRetry}>Retry</button>
        {onCancel && (
          <button type="button" onClick={onCancel} className="secondary">Cancel</button>
        )}
      </div>
    </div>
  );
}

function copyForCode(code: RoomJoinErrorCode): { title: string; body: string } {
  switch (code) {
    case 'handshake_timeout':
      return {
        title: 'Game did not respond',
        body: 'The Unity bridge failed to acknowledge our handshake. The game may not have loaded correctly.',
      };
    case 'signalr_failed':
      return {
        title: 'Could not reach the room server',
        body: 'We were unable to establish a real-time connection. Check your network and try again.',
      };
    case 'spawn_failed':
      return {
        title: 'Could not spawn your character',
        body: 'The game accepted you into the room but failed to create your avatar.',
      };
    case 'join_ack_timeout':
      return {
        title: 'Server did not confirm join',
        body: 'Our request to enter the room timed out before the server replied.',
      };
    default:
      return {
        title: 'Could not enter the room',
        body: 'An unknown error occurred while joining.',
      };
  }
}
